import Business from '@mui/icons-material/Business'
import Delete from '@mui/icons-material/Delete'
import {
  Card,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { VendorExclusion } from '@/types/rollup'

export type CompanyGroup = {
  dpOrgId: string
  companyName: string
  exclusions: VendorExclusion[]
}

/**
 * What a row shows for a vendor. Migrated vendors can legitimately have a
 * blank display_name pending a lazy backfill, so fall back to vendor_num —
 * same as RollupConfigVendors. Exported because VendorExclusionsTab sorts rows
 * by this, and a sort key that disagreed with the visible text would order the
 * list by something the user can't see.
 */
export function vendorExclusionLabel(exclusion: VendorExclusion): string {
  return exclusion.display_name || exclusion.vendor_num
}

type CompanyExclusionsCardProps = {
  group: CompanyGroup
  onUnexclude: (exclusionId: string) => void
}

export default function CompanyExclusionsCard({
  group,
  onUnexclude,
}: CompanyExclusionsCardProps) {
  return (
    <Card variant="outlined">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 2, py: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Business sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography sx={{ fontWeight: 500 }}>{group.companyName}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {group.exclusions.length} excluded
        </Typography>
      </Stack>
      <Divider />
      <Stack>
        {group.exclusions.map((exclusion, index) => {
          const label = vendorExclusionLabel(exclusion)
          return (
            <Stack key={exclusion.id}>
              {index > 0 && <Divider />}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 2, py: 1 }}
              >
                <Typography variant="body2">{label}</Typography>
                <Tooltip title="Unexclude">
                  <IconButton
                    size="small"
                    aria-label={`Unexclude ${label}`}
                    onClick={() => onUnexclude(exclusion.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          )
        })}
      </Stack>
    </Card>
  )
}
